import * as React from 'react';
import type { IconName } from '@cinuru/utils/theme';
import styled, { useTheme } from 'styled-components';
import { Box } from '@mui/material';

type IconProps = {
	id?: string;
	style?: React.CSSProperties;
	name: IconName;
	size?: string;
	color?: string;
	opacity?: number;
	gradient?: string;
	padding?: string;
	margin?: string;
	outline?: boolean;
	strokeWidth?: number;
	strokeLinecap?: 'butt' | 'round' | 'square';
	strokeLinejoin?: 'miter' | 'round' | 'bevel';
	rotation?: number;
	backgroundColor?: string;
};

type WrapperProps = {
	size?: string;
	padding?: string;
	margin?: string;
	backgroundColor?: string;
	opacity?: number;
};

type SVGWrapperProps = {
	rotation?: number;
};

const Wrapper = styled(Box)<WrapperProps>`
	width: ${(p) => p.size};
	height: ${(p) => p.size};
	padding: ${(p) => p.padding};
	margin: ${(p) => p.margin};
	background-color: ${(p) => p.backgroundColor};
	opacity: ${(p) => p.opacity};
`;

const SVGWrapper = styled(Box)<SVGWrapperProps>`
	transform: ${(p) => (p.rotation ? `rotate(${p.rotation}deg)` : 'none')};
`;

const useResolvedColor = (color: string | undefined, theme: any): string | undefined => {
	if (!color) return undefined;
	if (color.startsWith('$')) {
		// Resolve theme variable (e.g., "$neutral0" => "#ffffff")
		return theme.colorScheme?.[color.slice(1)] || undefined;
	}
	return color; // Assume it's a raw color value (e.g., "#fff" or "red")
};

const Icon = React.forwardRef<HTMLDivElement, IconProps>(
	(
		{
			id,
			style,
			name,
			size = '3rem',
			color,
			opacity = 1,
			padding = '0rem',
			margin = '0rem',
			outline,
			strokeWidth = 1,
			strokeLinecap = 'round',
			strokeLinejoin = 'round',
			rotation,
			backgroundColor = 'transparent',
		}: IconProps,
		ref
	) => {
		const theme = useTheme();

		const resolvedColor = useResolvedColor(color || '$neutral0', theme);

		if (!theme.iconPaths[name]) {
			console.warn(`Missing icon path for '${name}'`);
		}

		const pathValue = theme.iconPaths[name];
		let path: string;

		if (typeof pathValue === 'object') {
			path = pathValue.default || pathValue.web || '';
			if (!path) throw new Error(`Icon '${name}' is not properly defined.`);
		} else {
			path = pathValue;
		}

		return (
			<Wrapper
				ref={ref}
				id={id || undefined}
				margin={margin}
				size={size}
				padding={padding}
				style={style}
				backgroundColor={backgroundColor}
				opacity={opacity}
			>
				<SVGWrapper rotation={rotation}>
					<svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
						<path
							fill={outline ? 'none' : resolvedColor}
							stroke={outline ? resolvedColor : 'none'}
							fillRule={outline ? 'nonzero' : 'evenodd'}
							d={path}
							strokeWidth={outline ? strokeWidth : 0}
							strokeLinecap={strokeLinecap}
							strokeLinejoin={strokeLinejoin}
						/>
					</svg>
				</SVGWrapper>
			</Wrapper>
		);
	}
);

Icon.displayName = 'Icon';

export default React.memo(Icon);
